/**
 * schedulerV2Demo.js
 * ──────────────────────────────────────────────────────────────────────
 * Demo fixtures for the v2 scheduler edits (scheduler_edits_v2.md).
 * Used ONLY by demo-mode surfaces (LegacyEchoScheduler, SmartRooming /
 * WaitlistTab / SchedulerSettings when `isDemo`).
 *
 * Placed here (outside /components/echo/scheduler/) so that the
 * scheduler banned-literal scanner does not double-flag these names
 * — the scanner only walks that directory top-level. Real-mode render
 * paths never import this file.
 */

/* ── Rooming: Kanban + MA + Manager fixtures ── */
export const DEMO_ROOMS = [
  { id: 'R1', name: 'Room 1', status: 'occupied', patient: 'John Smith', provider: 'Dr. Chen', providerId: 'dr-chen', timeMin: 18, estRemaining: 8, type: 'Follow-up' },
  { id: 'R2', name: 'Room 2', status: 'occupied', patient: 'Maria Garcia', provider: 'Dr. Wilson', providerId: 'dr-wilson', timeMin: 11, estRemaining: 14, type: 'New Patient' },
  { id: 'R3', name: 'Room 3', status: 'available', patient: null, provider: null, providerId: null, timeMin: 0, estRemaining: 0, type: null },
  { id: 'R4', name: 'Room 4', status: 'available', patient: null, provider: null, providerId: null, timeMin: 0, estRemaining: 0, type: null },
];

export const DEMO_ROOMING_QUEUE = [
  { id: 'Q1', name: 'Robert Johnson', time: '10:00 AM', provider: 'Dr. Chen', providerId: 'dr-chen', type: 'Sick Visit', waitMin: 22, priority: 'high', status: 'in_waiting' },
  { id: 'Q2', name: 'Emily Davis', time: '10:30 AM', provider: 'Dr. Chen', providerId: 'dr-chen', type: 'Follow-up', waitMin: 5, priority: 'normal', status: 'arrived' },
];

export const DEMO_ROOMING_PROVIDERS = [
  { id: 'dr-chen', name: 'Dr. Chen', status: 'with_patient', room: 'Room 1', behindMin: 0, nextPatient: 'Robert Johnson' },
  { id: 'dr-wilson', name: 'Dr. Wilson', status: 'with_patient', room: 'Room 2', behindMin: 15, nextPatient: 'James Taylor' },
];

export const DEMO_ROOMING_ALERTS = [
  { id: 'a1', type: 'warning', text: 'Robert Johnson waiting 22+ min — prioritize next available room', action: 'Assign room', color: '#f59e0b' },
  { id: 'a2', type: 'lag', text: 'Dr. Wilson running 15 min behind — consider notifying 2:00 PM patient', action: 'Notify', color: '#f59e0b' },
  { id: 'a3', type: 'available', text: 'Room 3 available — assign next patient in queue', action: 'Assign', color: '#22c55e' },
];

/* ── Waitlist: patients with arrival/probability signals ── */
export const DEMO_WAITLIST_PATIENTS = [
  {
    id: 'wl-1',
    probability: 87,
    name: 'Sarah Chen',
    type: 'New Patient',
    addedAt: 'Mon Apr 14',
    daysWaiting: 4,
    arrival: '<15',
    insuranceActive: true,
    showRate: 94,
    careNeed: 'Annual Wellness (overdue)',
    bestMatch: 'Tomorrow 11:30 AM · Dr. Wilson',
  },
  {
    id: 'wl-2',
    probability: 72,
    name: 'Robert Johnson',
    type: 'Follow-up',
    addedAt: 'Tue Apr 15',
    daysWaiting: 3,
    arrival: '15-30',
    insuranceActive: true,
    showRate: 82,
    careNeed: null,
    bestMatch: 'Friday 2:00 PM · Dr. Chen',
  },
  {
    id: 'wl-3',
    probability: 61,
    name: 'Jennifer Lee',
    type: 'Follow-up',
    addedAt: 'Wed Apr 16',
    daysWaiting: 2,
    arrival: '30-60',
    insuranceActive: true,
    showRate: 76,
    careNeed: 'HbA1c due',
    bestMatch: 'Next week',
  },
  {
    id: 'wl-4',
    probability: 42,
    name: 'Michael Brown',
    type: 'New Patient',
    addedAt: 'Mon Apr 14',
    daysWaiting: 4,
    arrival: '60+',
    insuranceActive: false,
    showRate: 58,
    careNeed: null,
    bestMatch: null,
  },
];

export const DEMO_WAITLIST_APPROVALS = [
  {
    id: 'ap-1',
    patientName: 'Robert Johnson',
    type: 'Follow-up',
    provider: 'Dr. Wilson',
    date: 'Friday April 17',
    overbookIndex: 4,
    overbookLimit: 3,
    submittedBy: 'Front Desk',
    submittedMinAgo: 14,
    aiRecommendation: 'Approve',
    aiRationale:
      '2 of 3 current overbooked patients have low show probability; net capacity likely unchanged.',
  },
];

export const DEMO_WAITLIST_ANALYTICS = {
  totalToday: 12,
  notified: 5,
  booked: 3,
  declined: 1,
  stillWaiting: 3,
  avgWaitDays: 2.6,
  slotsFilledThisWeek: 6,
  revenueRecovered: '$900',
};

export const DEMO_VISIT_TYPES = [
  { id: 'new', label: 'New Patient' },
  { id: 'followup', label: 'Follow-up' },
  { id: 'sick', label: 'Sick Visit' },
  { id: 'annual', label: 'Annual Wellness' },
];

export const DEMO_PROVIDER_OPTIONS = [
  { id: 'dr-chen', label: 'Dr. Chen' },
  { id: 'dr-wilson', label: 'Dr. Wilson' },
];

/* ── Overbooking settings fixtures ── */
export const DEMO_OVERBOOK_LOCATIONS = [
  {
    id: 'main',
    name: 'Westside Medical — Main Office',
    locationCap: 5,
    providers: [
      {
        id: 'dr-chen',
        name: 'Dr. Sarah Chen',
        limit: 3,
        aiSuggested: 4,
        aiRationale:
          "Dr. Chen's 30-day show rate is 91%. A limit of 4 is statistically safe for her schedule.",
        weekOverbooked: 2,
        weekNoShows: 0,
        autoAdjust: false,
      },
      {
        id: 'dr-wilson',
        name: 'Dr. James Wilson',
        limit: 2,
        aiSuggested: 2,
        aiRationale:
          "Dr. Wilson's show rate has been 78% this month. Current limit of 2 is appropriate.",
        weekOverbooked: 1,
        weekNoShows: 1,
        autoAdjust: false,
      },
    ],
  },
];

/* ── Weekly Scheduler Review demo fixture ── */
export const DEMO_WEEKLY_REVIEW = {
  week_start: (() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + (d.getDay() === 0 ? -6 : 1));
    return d.toISOString().slice(0, 10);
  })(),
  narrative:
    "Overall capacity posture is strong this week: 1 provider has headroom to raise their overbooking limit and capture an estimated $180 of additional weekly revenue, while 1 provider's show rate dipped and should tighten to prevent scheduling gaps. The single highest-value action is applying Dr. Chen's +1 suggestion before Monday's schedule generation.",
  providers: [
    {
      id: 'dr-chen',
      name: 'Dr. Sarah Chen',
      current_limit: 3,
      ai_suggested: 4,
      show_rate: 91,
      total_appointments: 124,
      no_shows: 11,
      trend: 'higher',
      rationale:
        "Show rate 91% supports raising the limit by 1 — estimated +$180 weekly revenue, minimal no-show risk.",
    },
    {
      id: 'dr-wilson',
      name: 'Dr. James Wilson',
      current_limit: 3,
      ai_suggested: 2,
      show_rate: 74,
      total_appointments: 98,
      no_shows: 25,
      trend: 'lower',
      rationale:
        "Show rate dropped to 74% this month — reduce to avoid scheduling gaps.",
    },
    {
      id: 'dr-roberts',
      name: 'Dr. Priya Roberts',
      current_limit: 2,
      ai_suggested: 2,
      show_rate: 86,
      total_appointments: 87,
      no_shows: 12,
      trend: 'match',
      rationale:
        "Show rate 86% — current limit is appropriate; no action needed.",
    },
  ],
  generated_at: new Date().toISOString(),
};

/* ── Overbooking visibility for the demo Calendar view ── */
// Marks specific demo appointment ids as overbooked and tracks per-day
// overbooking counters for the week-view day-header pill.
export const DEMO_OVERBOOKED_APT_IDS = new Set(['a5', 'a8']);

export const DEMO_WEEK_OVERBOOK_BY_DATE = {
  14: { used: 2, limit: 3 },
  15: { used: 3, limit: 3 },
  16: { used: 0, limit: 3 },
  17: { used: 2, limit: 3 },
  18: { used: 4, limit: 3 },
  19: { used: 0, limit: 2 },
  20: { used: 0, limit: 2 },
};

/* ── Unified kanban flow items (user-addressable with status) ── */
// Each item carries a canonical `status` phase which drives the column.
// The Rooming view mutates these via onMoveTo(item, toStatus) — the
// LegacyEchoScheduler owns the state.
export const DEMO_FLOW_ITEMS = [
  { id: 'f1', name: 'John Smith', type: 'Follow-up', provider: 'Dr. Chen', providerId: 'dr-chen', time: '9:00 AM', roomName: 'Room 1', status: 'in_exam', hasContingency: true },
  { id: 'f2', name: 'Maria Garcia', type: 'New Patient', provider: 'Dr. Wilson', providerId: 'dr-wilson', time: '9:30 AM', roomName: 'Room 2', status: 'with_provider' },
  { id: 'f3', name: 'Robert Johnson', type: 'Sick Visit', provider: 'Dr. Chen', providerId: 'dr-chen', time: '10:00 AM', waitMin: 22, status: 'waiting_room' },
  { id: 'f4', name: 'Emily Davis', type: 'Follow-up', provider: 'Dr. Chen', providerId: 'dr-chen', time: '10:30 AM', waitMin: 5, status: 'arrived' },
  { id: 'f5', name: 'James Taylor', type: 'Wellness', provider: 'Dr. Wilson', providerId: 'dr-wilson', time: '11:00 AM', status: 'scheduled' },
  { id: 'f6', name: 'Sarah Williams', type: 'Follow-up', provider: 'Dr. Chen', providerId: 'dr-chen', time: '1:00 PM', status: 'scheduled' },
  { id: 'f7', name: 'Thomas Parker', type: 'Follow-up', provider: 'Dr. Chen', providerId: 'dr-chen', time: '8:30 AM', status: 'ready_checkout' },
];

/* ═══════════════════════════════════════════════════════════════════════
 * Echo Overbooking Engine spec v1.0 — demo fixtures
 * (echo_overbooking_engine_spec.md, April 2026)
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * Per-slot contingency state keyed by appointment id. Every overbooked
 * slot here has:
 *   - a waitlist patient held as contingency
 *   - an arrival-probability score (30% arrival + 25% show rate + ...)
 *   - a status lifecycle: 'offer_sent' → 'confirmed' | 'declined' → 'released'
 *   - a risk breakdown for the original patient (why was this slot flagged)
 */
export const DEMO_CONTINGENCIES = {
  a5: {
    waitlistPatient: 'Sarah Chen',
    waitlistPatientId: 'wl-1',
    arrivalProbability: 88,
    status: 'confirmed', // offer_sent | confirmed | declined
    aiActiveLabel: 'Confirmed — ready to fill',
    riskScore: 67,
    riskFactors: [
      { label: 'Prior no-shows', detail: '3 in 12 months', pts: 28 },
      { label: 'Appointment lead time', detail: 'Booked 45 days ago', pts: 14 },
      { label: 'Confirmation status', detail: 'Not confirmed (24h out)', pts: 12 },
      { label: 'Appointment type', detail: 'New patient', pts: 8 },
      { label: 'Day/time', detail: 'Friday PM', pts: 5 },
    ],
  },
  a8: {
    waitlistPatient: 'Robert Johnson',
    waitlistPatientId: 'wl-2',
    arrivalProbability: 72,
    status: 'offer_sent',
    aiActiveLabel: 'Offer sent — awaiting reply',
    riskScore: 42,
    riskFactors: [
      { label: 'Prior no-shows', detail: '1 in 12 months', pts: 10 },
      { label: 'Appointment lead time', detail: 'Booked 30 days ago', pts: 10 },
      { label: 'Confirmation status', detail: 'Not confirmed (24h out)', pts: 12 },
      { label: 'Appointment type', detail: 'New patient', pts: 8 },
      { label: 'Day/time', detail: 'Afternoon', pts: 2 },
    ],
  },
};

/**
 * Single active collision event (original + waitlist both arrived).
 * Drives the Collision Alert card shown in the Today / Front-Desk view.
 */
export const DEMO_COLLISION = {
  id: 'coll-1',
  active: true,
  providerName: 'Dr. Chen',
  slotTime: '10:00 AM',
  originalPatient: {
    name: 'Robert Johnson',
    status: 'checked_in',
    detail: 'Checked in · Room 1',
  },
  waitlistPatient: {
    name: 'Sarah Chen',
    status: 'arrived',
    detail: 'Arrived · waiting',
  },
  aiNextSlot: '1:00 PM with Dr. Chen',
  aiRecommendation: 'Book Sarah for 1:00 PM + apply goodwill credit',
  aiGoodwillAmount: '$10',
};

/**
 * Overbooking Efficiency Dashboard — aggregated metrics for Analytics tab.
 */
export const DEMO_OB_ANALYTICS = {
  windowLabel: 'Last 7 days',
  metrics: {
    fillRate: { value: 78, unit: '%', label: 'Fill rate', sub: '14 of 18 contingencies converted' },
    collisionRate: { value: 5.5, unit: '%', label: 'Collision rate', sub: 'Industry avg: 8%' },
    revenueRecovered: { value: '$2,100', unit: '', label: 'Revenue recovered', sub: 'vs potential no-show loss' },
    avgLeadTime: { value: 4.2, unit: 'h', label: 'Avg lead time to fill', sub: 'Offer → confirmation' },
    waitlistConversion: { value: 24, unit: '%', label: 'Waitlist conversion', sub: 'Seen via overbook' },
    aiAccuracy: { value: 71, unit: '%', label: 'AI accuracy', sub: '67% no-shows occurred as predicted' },
  },
  weeklyCard: {
    slotsFlagged: 23,
    contingenciesPlaced: 18,
    conversions: 14,
    collisions: 1,
    revenueRecovered: '$2,100',
    accuracy: 71,
    providerBreakdown: [
      { id: 'dr-chen', name: 'Dr. Chen', flags: 8, filled: 6, collisions: 0, warn: false },
      { id: 'dr-wilson', name: 'Dr. Wilson', flags: 10, filled: 8, collisions: 1, warn: true },
    ],
    recommendation:
      "Dr. Wilson's collision rate is slightly elevated. Consider reducing his limit from 3 to 2 until next month's data updates.",
    recommendationProviderId: 'dr-wilson',
    recommendationNewLimit: 2,
  },
};

/**
 * Extended per-provider overbook config (spec §Settings).
 * Keeps the previously-shipped DEMO_OVERBOOK_LOCATIONS shape intact but
 * layers on the v1.0 engine fields. The Settings UI is the authoritative
 * source of truth for these values during demo.
 */
export const DEMO_OB_PROVIDER_CONFIG = {
  'dr-chen': {
    overbookWindow: 'all_day',       // morning_only | all_day | afternoon_only | custom
    autoContactWaitlist: true,
    collisionProtocol: 'offer_goodwill', // offer_next_slot | offer_goodwill | manual_only
    goodwillAmount: 10,              // dollars
  },
  'dr-wilson': {
    overbookWindow: 'morning_only',
    autoContactWaitlist: false,
    collisionProtocol: 'offer_next_slot',
    goodwillAmount: 0,
  },
};

export const DEMO_OB_GLOBAL_RULES = {
  riskThreshold: 30,     // 20 | 30 | 40 | 50
  neverOverbook: {
    newPatient: true,
    procedure: false,
    extendedAbsence: false,
    confirmedRecent: true,
  },
  staffConfirmation: 'only_above_limit', // always | only_above_limit | never
};

/* ═══ Provider legend for multi-column grid (demo) ═══ */
export const DEMO_GRID_PROVIDERS = [
  { id: 'Dr. Chen', name: 'Dr. Chen', location: 'Westside Main', color: '#3b82f6', todayCount: 5 },
  { id: 'Dr. Wilson', name: 'Dr. Wilson', location: 'Westside Main', color: '#8b5cf6', todayCount: 3 },
  { id: 'Dr. Roberts', name: 'Dr. Roberts', location: 'Westside 2nd', color: '#f59e0b', todayCount: 1 },
];

/* ═══ Visit type catalog (demo) ═══ */
export const DEMO_VISIT_TYPE_CATEGORIES = {
  scheduling: 'SCHEDULING',
  billing: 'BILLING & CODING',
  care: 'CARE TYPE',
  cm: 'CARE MANAGEMENT PROGRAMS',
  comms: 'COMMUNICATION & WORKFLOW',
  specialty: 'SPECIALTY / REGULATORY',
};

export const DEMO_VISIT_TYPE_FLAGS = [
  { id: 'referringRequired', group: 'scheduling', label: 'Referring physician required', desc: 'Booking lightbox will prompt for a referring physician before confirming.' },
  { id: 'defaultType', group: 'scheduling', label: 'Default type of visit', desc: 'Appears in the primary tiles row in the booking lightbox.' },
  { id: 'nonBillable', group: 'billing', label: 'Non-billable', desc: 'Adds a "Non-billable" warning on the confirm screen and flags the appointment in RevQ.' },
  { id: 'accumulatedBilling', group: 'billing', label: 'Accumulated billing', desc: 'Combines charges into a rolling billing cycle.' },
  { id: 'nonMedical', group: 'billing', label: 'Non-medical type', desc: 'Suppresses clinical encounter creation.' },
  { id: 'ccdClaim', group: 'billing', label: 'CCD claim encounter', desc: 'Generates a CCD-formatted claim on completion.' },
  { id: 'emCode', group: 'billing', label: 'E/M code required', desc: 'Provider must select an E/M code before closing the encounter.' },
  { id: 'teleHealth', group: 'care', label: 'TeleHealth', desc: 'Adds a TeleHealth indicator and auto-generates a video link.' },
  { id: 'wellChild', group: 'care', label: 'Well child visit', desc: 'Enables the Well Child recurrence frequency settings.' },
  { id: 'wellness', group: 'care', label: 'Wellness visit', desc: 'Enables the Wellness recurrence frequency settings.' },
  { id: 'obVisit', group: 'care', label: 'OB visit', desc: 'Tags the visit for obstetric reporting.' },
  { id: 'obInitial', group: 'care', label: 'OB initial', desc: 'Marks as the first OB encounter in a pregnancy series.' },
  { id: 'hospital', group: 'care', label: 'Hospital visit', desc: 'Tags visit as hospital-based for reporting.' },
  { id: 'group', group: 'care', label: 'Group therapy', desc: 'Allows multiple patients in the same slot with this provider.' },
  { id: 'rpm', group: 'cm', label: 'RPM (Remote Patient Monitoring)', desc: 'Includes this visit in RPM program tracking.' },
  { id: 'tcm', group: 'cm', label: 'TCM (Transitional Care Management)', desc: 'Counts toward TCM program billing and workflow.' },
  { id: 'mtm', group: 'cm', label: 'MTM (Medication Therapy Management)', desc: 'Enables medication therapy workflow templates.' },
  { id: 'careCoord', group: 'cm', label: 'Care coordinator visit', desc: 'Routes to care coordinator dashboard.' },
  { id: 'phone', group: 'comms', label: 'Phone message / encounter', desc: 'Classifies as a phone encounter.' },
  { id: 'phoneDefault', group: 'comms', label: 'Default phone message / encounter', desc: 'Used as the default when creating phone encounters.' },
  { id: 'labReview', group: 'comms', label: 'Lab result review', desc: 'Creates lab-review task on completion.' },
  { id: 'tocInpatient', group: 'comms', label: 'Transfer of care — inpatient', desc: 'Tags as inpatient TOC.' },
  { id: 'tocAmbulatory', group: 'comms', label: 'Transfer of care — ambulatory', desc: 'Tags as ambulatory TOC.' },
  { id: 'fpe', group: 'comms', label: 'Force screen for patient equipment (FPE)', desc: 'Requires screening for patient-supplied equipment.' },
  { id: 'dataMigration', group: 'comms', label: 'Data migration', desc: 'Reserved for historical data imports; non-clinical.' },
  { id: 'atteindre', group: 'specialty', label: 'Atteindre', desc: 'Atteindre program participation flag.' },
  { id: 'doh', group: 'specialty', label: 'DOH', desc: 'Flags for Department of Health reporting.' },
  { id: 'nycM11q', group: 'specialty', label: 'NYC M-11Q', desc: 'NYC M-11Q specialty reporting flag.' },
  { id: 'nyia', group: 'specialty', label: 'NYIA', desc: 'NYIA assessment flag.' },
];

export const DEMO_CLINICAL_SECTIONS = {
  'INTAKE & HISTORY': [
    { id: 'chiefComplaint', label: 'Chief Complaint', desc: "Patient's stated reason for visit" },
    { id: 'hpi', label: 'HPI', desc: 'History of present illness narrative' },
    { id: 'ros', label: 'ROS', desc: 'Review of systems' },
    { id: 'physical', label: 'Physical Exam', desc: 'Provider physical examination' },
    { id: 'immunization', label: 'Immunization', desc: 'Immunization record review' },
    { id: 'diet', label: 'Diet', desc: 'Dietary history + recommendations' },
    { id: 'exercise', label: 'Exercise', desc: 'Exercise history + recommendations' },
    { id: 'anesthesia', label: 'Anesthesia', desc: 'Anesthesia history + plan' },
  ],
  'ASSESSMENT & ORDERS': [
    { id: 'assessment', label: 'Assessment / ICD', desc: 'Diagnosis codes and clinical assessment' },
    { id: 'orders', label: 'Orders / Requisitions', desc: 'Lab orders, imaging, referrals' },
    { id: 'procedures', label: 'Procedures', desc: 'Procedural documentation' },
    { id: 'quickNotes', label: 'Quick Notes', desc: 'Short notes for rapid encounters' },
    { id: 'referral', label: 'Referral / Follow Up', desc: 'Referral and follow-up planning' },
  ],
  'MEDICATIONS & VITALS': [
    { id: 'vitals', label: 'Vitals', desc: 'Recorded vitals for encounter' },
    { id: 'medications', label: 'Medications / Rx', desc: 'Active meds + Rx ordering' },
    { id: 'emCodeSec', label: 'E/M Code', desc: 'Evaluation & management code selection' },
  ],
  'BILLING & PAYMENT': [
    { id: 'payment', label: 'Payment', desc: 'Copay and billing information for this encounter' },
  ],
};

/* ═══ Lightbox: patient search + locations (demo) ═══ */
export const DEMO_LIGHTBOX_PATIENTS = [
  { id: 'P-1001', name: 'John Smith', dob: '03/15/1962', phone: '(555) 123-4567', lastVisit: '2 weeks ago', preferredProvider: 'Dr. Chen', preferredLocation: 'main', preferredType: 'Follow-up', referringMd: 'Dr. James Park (NYU Langone)' },
  { id: 'P-1002', name: 'Maria Garcia', dob: '07/22/1985', phone: '(555) 234-5678', lastVisit: 'New Patient', preferredProvider: 'Dr. Wilson', preferredLocation: 'main', preferredType: 'New Patient', referringMd: null },
  { id: 'P-1003', name: 'Robert Johnson', dob: '01/10/1958', phone: '(555) 345-6789', lastVisit: '2 months ago', preferredProvider: 'Dr. Chen', preferredLocation: 'main', preferredType: 'Sick Visit', referringMd: null },
  { id: 'P-1004', name: 'Emily Davis', dob: '11/30/1978', phone: '(555) 456-7890', lastVisit: '3 months ago', preferredProvider: 'Dr. Chen', preferredLocation: 'main', preferredType: 'Follow-up', referringMd: null },
  { id: 'P-1005', name: 'James Taylor', dob: '09/08/1970', phone: '(555) 567-8901', lastVisit: '6 months ago', preferredProvider: 'Dr. Wilson', preferredLocation: 'main', preferredType: 'Wellness', referringMd: null },
  { id: 'P-1006', name: 'John Smith', dob: '07/22/1985', phone: '(555) 999-0000', lastVisit: '6 months ago', preferredProvider: 'Dr. Wilson', preferredLocation: 'main', preferredType: 'Follow-up', referringMd: null },
  { id: 'P-1007', name: 'Sarah Miller', dob: '04/18/1991', phone: '(555) 777-1212', lastVisit: 'New Patient', preferredProvider: 'Dr. Chen', preferredLocation: 'main', preferredType: 'New Patient', referringMd: 'Dr. James Park (NYU Langone)' },
];

export const DEMO_LIGHTBOX_LOCATIONS = [
  { id: 'main', name: 'Main Office', address: '100 Medical Pkwy', providers: ['Dr. Chen', 'Dr. Wilson', 'Dr. Roberts'] },
  { id: 'annex', name: 'Westside Annex', address: '220 West Park Ave', providers: ['Dr. Chen', 'Dr. Roberts'] },
];

export const DEMO_LIGHTBOX_OPEN_SLOTS = [
  { time: '11:30 AM', provider: 'Dr. Chen', location: 'main', recommended: true },
  { time: '12:00 PM', provider: 'Dr. Chen', location: 'main', recommended: false },
  { time: '2:30 PM', provider: 'Dr. Wilson', location: 'main', recommended: false },
  { time: '3:30 PM', provider: 'Dr. Chen', location: 'annex', recommended: false },
];

export const DEMO_VISIT_TYPES_CATALOG = [
  { id: 'vt-new', name: 'New Patient', abbreviation: 'NP', color: 'Blue', type: 'Standard', status: 'enabled', profile: 'Normal', defaultDuration: 45, showAsDefault: true, physicianLabel: 'Physician', maLabel: 'Medical Asst', flags: { referringRequired: true, defaultType: true, nonBillable: false, emCode: true }, recurrence: { years: 0, months: 0, days: 0 }, sections: { chiefComplaint: true, hpi: true, assessment: true, orders: true, payment: true }, image: { perLine: 2, width: 200, height: 250 } },
  { id: 'vt-follow', name: 'Follow-up', abbreviation: 'FU', color: 'Green', type: 'Standard', status: 'enabled', profile: 'Normal', defaultDuration: 30, showAsDefault: true, physicianLabel: 'Physician', maLabel: 'Medical Asst', flags: { defaultType: true }, recurrence: { years: 0, months: 0, days: 0 }, sections: { chiefComplaint: true, hpi: true, assessment: true, payment: true }, image: { perLine: 2, width: 200, height: 250 } },
  { id: 'vt-sick', name: 'Sick Visit', abbreviation: 'SV', color: 'Red', type: 'Standard', status: 'enabled', profile: 'Normal', defaultDuration: 20, showAsDefault: true, physicianLabel: 'Physician', maLabel: 'Medical Asst', flags: {}, recurrence: { years: 0, months: 0, days: 0 }, sections: { chiefComplaint: true, hpi: true, assessment: true }, image: { perLine: 2, width: 200, height: 250 } },
  { id: 'vt-wellness', name: 'Wellness', abbreviation: 'WV', color: 'Amber', type: 'Standard', status: 'enabled', profile: 'Normal', defaultDuration: 45, showAsDefault: true, physicianLabel: 'Physician', maLabel: 'Medical Asst', flags: { wellness: true, defaultType: true }, recurrence: { years: 1, months: 0, days: 0 }, sections: { chiefComplaint: true, hpi: true, assessment: true, vitals: true, payment: true }, image: { perLine: 2, width: 200, height: 250 } },
  { id: 'vt-well-child', name: 'Well Child', abbreviation: 'WC', color: 'Teal', type: 'Standard', status: 'enabled', profile: 'Normal', defaultDuration: 30, showAsDefault: false, physicianLabel: 'Pediatrician', maLabel: 'MA', flags: { wellChild: true }, recurrence: { years: 0, months: 12, days: 0 }, sections: { chiefComplaint: true, hpi: true, immunization: true, assessment: true, vitals: true }, image: { perLine: 2, width: 200, height: 250 } },
  { id: 'vt-tele', name: 'TeleHealth', abbreviation: 'TH', color: 'Purple', type: 'Standard', status: 'enabled', profile: 'Normal', defaultDuration: 30, showAsDefault: false, physicianLabel: 'Physician', maLabel: 'Medical Asst', flags: { teleHealth: true }, recurrence: { years: 0, months: 0, days: 0 }, sections: { chiefComplaint: true, hpi: true, assessment: true }, image: { perLine: 2, width: 200, height: 250 } },
  { id: 'vt-phone', name: 'Phone Encounter', abbreviation: 'PE', color: 'Gray', type: 'Standard', status: 'enabled', profile: 'Normal', defaultDuration: 15, showAsDefault: false, physicianLabel: 'Physician', maLabel: 'Medical Asst', flags: { phone: true, phoneDefault: true, nonBillable: false }, recurrence: { years: 0, months: 0, days: 0 }, sections: { quickNotes: true }, image: { perLine: 2, width: 200, height: 250 } },
];
